// MELDINGEN INN TIL SKADE — og EMCON-grensa den ligger bak.
//
// ══ TO BESLUTNINGER, HOLDT FRA HVERANDRE ═══════════════════════════════════
// Å SKRIVE en melding og å KRINGKASTE den er to ulike ting for et jegerlag bak
// fiendens linjer, og de skal tas hver for seg. Derfor:
//   * «Lag melding fra denne sekvensen» (i sekvensanalysen) stråler ikke — den
//     fyller ut utkastet her.
//   * Sendeknappen her stråler, og den er deaktivert til EMCON-bryteren i
//     toppstripa er slått PÅ. Er sending av, legges meldingen i køen, og køen
//     tømmes bare av en bevisst handling. Det finnes INGEN automatisk periodisk
//     opplasting i denne appen.
//
// ══ HVORFOR KLARTEKSTEN ER LIKE VIKTIG SOM SENDINGEN ═══════════════════════
// Den viktigste transporten for et jegerlag er ikke en datalenke — det er en
// stemme på en radio. Derfor bygges meldingen ALLTID i to former samtidig:
// `CombatReport` (det som sendes, og som COP tegner) og de åtte linjene
// DTG/FRA/TIL/HVEM/HVA/HVOR/NÅR/HVORLEDES som kan leses opp. Klarteksten kan
// kopieres uten bro, uten innlogging og uten rettighet — den virker når alt annet
// er nede, og det er nettopp da den trengs.
//
// Begrunnelsen for valget av kontrakt (og hvorfor SALUTE ble forkastet) står i
// `lib/melding.ts`. Den skal ikke gjentas her.
import { can } from '@skade/rbac';
import {
  div, felt, h, knapp, kopierknapp, kvittering, merke, nv, p, sett, span, tomt,
} from '@skade/sensorflate';
import { hendelserITid, kjenteSensorer } from '../lib/lager.js';
import { analyser } from '../lib/sekvens.js';
import { MAKS, lagUtkast, radiotekst, tilCombatReport, tilStridsmelding, type Meldingsutkast } from '../lib/melding.js';
import { ikon } from '../ui/ikoner.js';
import type { Kontekst } from './kontekst.js';

/**
 * Skjermens egen tilstand: jegerens EGNE ORD, og de redigerte feltene.
 *
 * Den ligger i modulen og ikke i lageret av samme grunn som i dekningsskjermen —
 * dette er noe jegeren skriver, ikke noe broen leverer. Nøkkelen er kandidatens
 * id, slik at teksten IKKE følger med når han bytter sekvens: en fritekst som
 * dukker opp under en annen hendelse er den enkleste måten å sende feil melding.
 */
const egneOrd = new Map<string, string>();
const overstyrt = new Map<string, Partial<Meldingsutkast['felter']>>();
/** Meldinger som er levert til køen/broen i denne økta, med tidspunkt. */
const sendte = new Map<string, { tsMs: number; iKo: boolean; meldingId: string }>();

export interface Meldingssammendrag {
  harUtkast: boolean;
  sendte: number;
}

export function tegnMelding(vert: HTMLElement, ctx: Kontekst): Meldingssammendrag {
  const t = ctx.tilstand();
  const naa = ctx.naa();
  const valgtId = ctx.valgtForMelding();
  const sensorer = kjenteSensorer(t);
  const bilde = analyser(hendelserITid(t), sensorer, naa);
  const kandidat = valgtId ? bilde.kandidater.find((k) => k.id === valgtId) ?? null : null;

  if (!kandidat) {
    sett(
      vert,
      tomt(
        'Ingen melding under arbeid',
        bilde.kandidater.length === 0
          ? 'Meldingsutkastet fylles ut fra en sekvens. Ingen sekvenser finnes ennå — når en utløsning kommer inn, kan du lage melding fra den.'
          : 'Trykk «Lag melding fra denne sekvensen» på et sekvenskort. Feltene blir forhåndsfylt med tid, sted og hva sensorene målte, og du legger til dine egne ord.',
        { ikon: ikon('melding') },
      ),
    );
    return { harUtkast: false, sendte: sendte.size };
  }

  const lag = t.post;
  const fra = lag?.kallesignal ?? lag?.navn ?? '(ukjent avsender — broen har ikke meldt hvilket lag dette er)';
  const utkast = lagUtkast(kandidat, sensorer, {
    fra,
    ...(egneOrd.get(kandidat.id) !== undefined ? { egneOrd: egneOrd.get(kandidat.id)! } : {}),
    naaMs: naa,
  });
  // Jegerens egne redigeringer overstyrer utkastet, felt for felt. Utkastet
  // regnes likevel om hver tegning, slik at et felt han IKKE har rørt følger de
  // ferskeste tallene fra kontrakten.
  const felter = { ...utkast.felter, ...(overstyrt.get(kandidat.id) ?? {}) };
  const helt: Meldingsutkast = { ...utkast, felter };

  const meldingId = `ull:${kandidat.id}`;
  const strids = tilStridsmelding(helt, meldingId, ctx.operatornavn(), naa);
  const rapport = tilCombatReport(helt, meldingId, ctx.operatornavn(), naa);

  const rot = div('mel');
  rot.append(tegnTopp(kandidat.id, helt, ctx));
  rot.append(tegnFelter(kandidat.id, helt, ctx));
  rot.append(tegnEgneOrd(kandidat.id, ctx));
  rot.append(tegnKlartekst(strids, ctx));
  rot.append(tegnGrunnlag(helt));
  rot.append(tegnSend(meldingId, rapport, strids, ctx));
  sett(vert, rot);
  return { harUtkast: true, sendte: sendte.size };
}

// ── Topp ────────────────────────────────────────────────────────────────────

function tegnTopp(kandidatId: string, u: Meldingsutkast, ctx: Kontekst): HTMLElement {
  const rot = div('mel-topp');
  const hode = div('mel-hode');
  hode.append(ikon('melding'));
  hode.append(h(3, 'mel-tittel', 'Stridsmelding — utkast'));
  hode.append(merke('MELDING', 'meldingstype'));
  hode.append(div('mel-rom'));
  hode.append(knapp('Forkast utkastet', () => {
    egneOrd.delete(kandidatId);
    overstyrt.delete(kandidatId);
    ctx.velgForMelding(null);
    ctx.kunngjør('Utkastet er forkastet. Ingenting ble sendt.');
    ctx.tegnPåNytt();
  }, { variant: 'stille', ikon: ikon('kryss') }));
  rot.append(hode);
  rot.append(p(
    'mel-brød',
    'Utkastet er bygget av tallene i kontraktens eget svar — ikke av gjetninger. Der analysen ikke kan svare, står det i '
    + 'meldingen at det er ubestemt. Endre fritt: dine ord vinner alltid over utkastet.',
  ));
  const sendt = sendte.get(`ull:${kandidatId}`);
  if (sendt) {
    rot.append(nv(
      'Levert',
      sendt.iKo
        ? 'Ligger i EMCON-køen — IKKE sendt ennå'
        : `Sendt til broen ${new Date(sendt.tsMs).toLocaleTimeString('no')}`,
      sendt.iKo ? 'advarsel' : 'ok',
    ));
  }
  if (u.lat === undefined) {
    rot.append(p('mel-advarsel', 'Meldingen har ingen posisjon. SKADE kan ikke tegne den i kartet — se HVOR-feltet.'));
  }
  return rot;
}

// ── De åtte linjene ─────────────────────────────────────────────────────────

const FELTNAVN: Record<keyof Meldingsutkast['felter'], string> = {
  fra: 'FRA (avsender)',
  til: 'TIL (mottaker)',
  hvem: 'HVEM (hvem så du)',
  hva: 'HVA (hendelse)',
  hvor: 'HVOR (sted)',
  naar: 'NÅR (tidspunkt hendelsen inntraff)',
  hvorledes: 'HVORLEDES (detaljer)',
};

/** Felt som skal ha flere linjer — samme inndeling som `REPORT_FIELDS.MELDING`. */
const LANGE: readonly (keyof Meldingsutkast['felter'])[] = ['hva', 'hvorledes'];

function tegnFelter(kandidatId: string, u: Meldingsutkast, ctx: Kontekst): HTMLElement {
  const rot = div('mel-bolk');
  rot.append(h(3, 'mel-bolkhode', 'Feltene som sendes'));
  for (const nøkkel of Object.keys(FELTNAVN) as (keyof Meldingsutkast['felter'])[]) {
    const verdi = u.felter[nøkkel];
    const maks = MAKS[nøkkel];
    const rad = div('mel-felt');
    const etikett = document.createElement('label');
    etikett.className = 'mel-etikett';
    etikett.textContent = FELTNAVN[nøkkel];
    rad.append(etikett);

    const inn = LANGE.includes(nøkkel)
      ? document.createElement('textarea')
      : document.createElement('input');
    inn.className = 'mel-inn';
    inn.value = verdi;
    inn.maxLength = maks;
    if (inn instanceof HTMLTextAreaElement) inn.rows = nøkkel === 'hvorledes' ? 8 : 3;
    // `change` og ikke `input`: hele flata tegnes om hvert sekund, og en
    // omtegning midt i et ord ville flyttet markøren. Verdien lagres når feltet
    // forlates, og teksten står trygt i DOM-en imens.
    inn.addEventListener('change', () => {
      const forrige = overstyrt.get(kandidatId) ?? {};
      overstyrt.set(kandidatId, { ...forrige, [nøkkel]: inn.value });
      ctx.kunngjør(`${FELTNAVN[nøkkel]} er endret`);
      ctx.tegnPåNytt();
    });
    etikett.htmlFor = `mel-${nøkkel}`;
    inn.id = `mel-${nøkkel}`;
    rad.append(inn);
    // Tegntelleren er ikke pynt: `StridsmeldingSchema` AVVISER et for langt felt,
    // og en melding som ikke kan bygges er en melding som ikke går ut. Grensen
    // skal derfor være synlig FØR den treffes.
    rad.append(span('mel-teller', `${verdi.length}/${maks} tegn`));
    rot.append(rad);
  }
  return rot;
}

// ── Jegerens egne ord ───────────────────────────────────────────────────────

/**
 * Fritekstfeltet som settes ØVERST i HVORLEDES.
 *
 * Det er et eget felt og ikke bare redigering av HVORLEDES, fordi de to tjener
 * ulike formål: HVORLEDES inneholder kontraktens utledning, som skal kunne
 * regenereres når nye utløsninger kommer inn. Jegerens vurdering skal ALDRI
 * regenereres bort. Blir meldingen for lang, er det utledningen som kappes —
 * jegerens ord kan ikke gjenskapes av mottakeren.
 */
function tegnEgneOrd(kandidatId: string, ctx: Kontekst): HTMLElement {
  const rot = div('mel-bolk');
  rot.append(h(3, 'mel-bolkhode', 'Dine egne ord'));
  rot.append(p(
    'mel-brød',
    'Det du skriver her legges FØRST i HVORLEDES, foran forbeholdet og kontraktens utledning. Sensorene sier hva som skjedde; '
    + 'bare du kan si hva du tror det betyr, og hva du gjør nå.',
  ));
  const inn = document.createElement('textarea');
  inn.className = 'mel-inn';
  inn.id = 'mel-egneord';
  inn.rows = 4;
  inn.value = egneOrd.get(kandidatId) ?? '';
  inn.placeholder = 'F.eks.: «Vurderer dette som forpost på hovedaksen. Blir liggende. Ber om at UGS-4 byttes batteri ved neste kurering.»';
  inn.addEventListener('change', () => {
    egneOrd.set(kandidatId, inn.value);
    // Utkastet regnes om, og HVORLEDES får de nye ordene øverst — men bare hvis
    // jegeren ikke har overstyrt HVORLEDES manuelt. Da er hans egen tekst der
    // fasit, og vi skal ikke overskrive den.
    ctx.kunngjør('Egne ord er lagt inn i utkastet');
    ctx.tegnPåNytt();
  });
  rot.append(inn);
  if (overstyrt.get(kandidatId)?.hvorledes !== undefined) {
    rot.append(p(
      'mel-advarsel',
      'Du har redigert HVORLEDES manuelt. Da settes IKKE disse ordene inn automatisk — din egen tekst i HVORLEDES vinner. '
      + 'Vil du ha dem inn, skriv dem der.',
    ));
  }
  return rot;
}

// ── Klarteksten ─────────────────────────────────────────────────────────────

function tegnKlartekst(strids: ReturnType<typeof tilStridsmelding>, ctx: Kontekst): HTMLElement {
  const rot = div('mel-bolk');
  rot.append(h(3, 'mel-bolkhode', 'Klartekst — les opp over samband'));
  if (!strids) {
    rot.append(p(
      'mel-advarsel',
      'Meldingen kan IKKE bygges: minst ett felt er for langt eller tomt for kontraktens skjema. Kort ned feltene over — '
      + 'tegntelleren viser hvilket.',
    ));
    return rot;
  }
  const tekst = radiotekst(strids);
  // `<pre>` med `textContent`: åtte linjer i fast bredde er formen man leser opp
  // fra, og linjeskiftene ER informasjonen. Aldri `innerHTML`.
  const pre = document.createElement('pre');
  pre.className = 'mel-klartekst';
  pre.textContent = tekst;
  rot.append(pre);
  rot.append(p(
    'mel-note',
    'HIMMELRETNINGER og ordet «IKKE» står med STORE bokstaver etter doktrinen — kontrakten gjør det automatisk. '
    + 'STEDSNAVN og KODENAVN må du selv skrive med versaler; det kan ikke utledes.',
  ));
  rot.append(kopierknapp(tekst, 'Klarteksten', (m, v) => {
    kvittering(m, v);
    ctx.kunngjør(m);
  }));
  return rot;
}

// ── Grunnlaget ──────────────────────────────────────────────────────────────

function tegnGrunnlag(u: Meldingsutkast): HTMLElement {
  const rot = div('mel-bolk');
  rot.append(h(3, 'mel-bolkhode', 'Hvor feltene kommer fra'));
  // Uten denne bolken ville utkastet vært magi. En jeger som skal stå for
  // meldingen må kunne se hvilket tall som ble hentet hvor.
  for (const g of u.grunnlag) rot.append(p('mel-grunnlag', g));
  return rot;
}

// ── Sending ─────────────────────────────────────────────────────────────────

/**
 * Sendeknappen. FEM ting kan sperre den, og de sies HVER FOR SEG:
 *   1. ØVING PÅGÅR — da er køen STENGT, og knappen er faktisk deaktivert,
 *   2. meldingen kan ikke bygges (skjemafeil),
 *   3. EMCON: sending er av — da kan den legges i kø, men ikke sendes,
 *   4. ingen rolle / manglende rettighet (klientsjekken er kosmetisk; hubben
 *      håndhever autoritativt, grunnloven §4),
 *   5. ingen bro tilkoblet.
 *
 * Å slå dem sammen til «kan ikke sende» ville vært den enkleste veien til at
 * jegeren tror appen er i stykker når det egentlig bare er radiotausheten som
 * gjelder.
 *
 * ══ HVORFOR ØVING ER DEN ENESTE SOM DEAKTIVERER KNAPPEN ════════════════════
 * De fire andre sperrene stopper SENDINGEN, ikke køen: å legge en melding i køen
 * er den handlingen som ikke stråler, og den skal alltid være mulig. Øving er
 * annerledes — der skal ingenting inn i køen i det hele tatt (krav 4 i
 * `@skade/sensorflate/ovelse.ts`), fordi en kø med oppdiktede meldinger overlever
 * at øvingen slås av og går ut som ekte neste gang sending slås på. Knappen er
 * derfor faktisk deaktivert, med grunnen skrevet ut under.
 */
function tegnSend(
  meldingId: string,
  rapport: ReturnType<typeof tilCombatReport>,
  strids: ReturnType<typeof tilStridsmelding>,
  ctx: Kontekst,
): HTMLElement {
  const rot = div('mel-bolk mel-send');
  rot.append(h(3, 'mel-bolkhode', 'Send til SKADE'));

  const s = ctx.sending();
  const okt = ctx.okt();
  const lag = ctx.tilstand().post;
  const org = lag?.distrikt ? { district: lag.distrikt, area: lag.omrade } : undefined;
  // RESSURSVALGET: `@skade/rbac` har ingen `'report'`-ressurs, så en stridsmelding
  // sjekkes mot `'track'` (skriv, minsterolle «menig»). Det er det nærmeste og det
  // riktigste: en melding blir et objekt i situasjonsbildet, og enhver soldat SKAL
  // kunne melde. Ville vi valgt `'target_list'` (minsterolle «områdesjef»), kunne
  // ikke et jegerlag rapportert i det hele tatt — som er absurd.
  // Se rapporten: rbac bør få en egen `'report'`-ressurs. Den ligger i packages/,
  // som denne oppgaven ikke skal endre.
  const harRett = okt.bruker !== null && can(okt.bruker, 'write', { resource: 'track', org });
  const tilkoblet = ctx.tilkobling().tilstand === 'tilkoblet';

  const tall = div('mel-tallrad');
  tall.append(felt('Sending (EMCON)', s.ovelse
    ? 'LÅST — øving pågår'
    : s.pa ? 'PÅ — appen kan stråle' : 'AV — radiotaushet', s.pa && !s.ovelse ? 'sender' : 'stille'));
  tall.append(felt('I kø', String(s.iKo)));
  tall.append(felt('Sist strålt', s.sistSendtMs === null ? 'Aldri i denne økta' : new Date(s.sistSendtMs).toLocaleTimeString('no')));
  if (strids) tall.append(felt('Størrelse', `${radiotekst(strids).length} tegn`));
  rot.append(tall);

  // ØVING FØRST, før alt annet: den er den ENE sperren som stenger køen, og den
  // skal ikke kunne forveksles med radiotaushet (der meldingen faktisk blir
  // liggende klar). Klarteksten over kan fortsatt kopieres og leses opp — den er
  // riktignok bygget av oppdiktede tall, og banneret over flata sier det.
  if (s.ovelse) {
    rot.append(p(
      'mel-advarsel',
      'ØVING PÅGÅR — denne meldingen kan verken sendes eller legges i køen. Den er bygget av oppdiktede '
      + 'utløsninger, og en oppdiktet melding i SKADE er en falsk melding noen kan handle på. Slå av '
      + 'øvingsdata i banneret øverst for å melde på ekte grunnlag.',
    ));
    rot.append(knapp('Send melding til SKADE', undefined, {
      variant: 'primær', ikon: ikon('sending-av'), deaktivert: true,
      tittel: 'Køen er stengt så lenge øvingsdata står på.',
    }));
    return rot;
  }

  if (!rapport.ok) {
    rot.append(p('mel-advarsel', rapport.grunn));
    rot.append(knapp('Send melding til SKADE', undefined, {
      variant: 'primær', ikon: ikon('sending'), deaktivert: true, tittel: rapport.grunn,
    }));
    return rot;
  }

  const sperrer: string[] = [];
  if (!s.pa) {
    sperrer.push(
      'EMCON: sending er slått AV. Meldingen kan legges i køen nå og går ut i det du slår på sending i toppstripa. '
      + 'Appen stråler ikke før du gjør det.',
    );
  }
  if (!okt.bruker) {
    sperrer.push('ULL har ingen innlogging, så rollen din er ukjent. Meldingen kan ikke sendes herfra — kopiér klarteksten og meld den inn over samband.');
  } else if (!harRett) {
    sperrer.push('Rollen din har ikke skriverett på meldinger. Send via en som har, eller les klarteksten opp.');
  }
  if (!tilkoblet) {
    sperrer.push(`Ingen ULL-bro tilkoblet (${ctx.tilkobling().tilstand}). Meldingen er bygget og kan kopieres, men den kan ikke leveres nå.`);
  }

  const rad = div('mel-knapperad');
  rad.append(knapp(s.pa ? 'Send melding til SKADE NÅ' : 'Legg meldingen i køen', () => {
    const ok = ctx.send({
      type: 'melding',
      meldingId,
      meldingstype: 'MELDING',
      felter: rapport.rapport.fields,
      ...(rapport.rapport.lat !== undefined ? { lat: rapport.rapport.lat } : {}),
      ...(rapport.rapport.lon !== undefined ? { lon: rapport.rapport.lon } : {}),
      tsMs: rapport.rapport.tsMs,
      klartekst: strids ? radiotekst(strids) : '',
    });
    sendte.set(meldingId, { tsMs: Date.now(), iKo: !ok, meldingId });
    if (ok) {
      kvittering('Meldingen er sendt til broen. Appen STRÅLTE nå.');
      ctx.kunngjør('Meldingen er sendt. Appen strålte.');
    } else {
      kvittering('Meldingen ligger i køen — ingenting er sendt.', 'feil');
      ctx.kunngjør('Meldingen ligger i køen. Ingenting er sendt.');
    }
    ctx.tegnPåNytt();
  }, {
    variant: 'primær',
    ikon: ikon(s.pa ? 'sending' : 'sending-av'),
    // Køen skal ALLTID kunne fylles — det er den handlingen som ikke stråler.
    // Bare selve sendingen er sperret, og det er `send()` i main.ts som avgjør.
    deaktivert: false,
    tittel: s.pa
      ? 'STRÅLER: meldingen går ut på lenken med en gang.'
      : 'Legger meldingen i køen. Ingenting sendes før du slår på sending.',
  }));
  rot.append(rad);

  for (const x of sperrer) rot.append(p('mel-sperre', x));
  return rot;
}
